import {
  eventLabels,
  statusConfig,
  statusDescriptions,
} from '../constants/paymentLinks.constants';
import { StatusColor } from '../types/paymentLinks.types';

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  currency: 'BRL',
  style: 'currency',
});

const dateTimeFormatter = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const dateFormatter = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const dateShortFormatter = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
});

const timeFormatter = new Intl.DateTimeFormat('pt-BR', {
  hour: '2-digit',
  minute: '2-digit',
});

const statusAliases: Record<string, string> = {
  ABERTO: 'open',
  AGUARDANDO: 'payment_submitted',
  ANALISE: 'analyzing',
  ANALYZING: 'analyzing',
  APROVADO: 'paid',
  APPROVED: 'paid',
  CHECKOUT_INICIADO: 'checkout_started',
  CHECKOUT_STARTED: 'checkout_started',
  CREATED: 'created',
  CRIADO: 'created',
  EM_ANALISE: 'analyzing',
  EM_PROCESSAMENTO: 'processing',
  ERRO: 'error',
  ERROR: 'error',
  EXPIRADO: 'expired',
  EXPIRED: 'expired',
  FALHA: 'error',
  FAILED: 'rejected',
  IDENTIFICADO: 'identified',
  IDENTIFIED: 'identified',
  INVALID: 'invalid',
  INVALIDO: 'invalid',
  OPEN: 'open',
  PAGO: 'paid',
  PAID: 'paid',
  PAGAMENTO_ENVIADO: 'payment_submitted',
  PAGAMENTO_INICIADO: 'checkout_started',
  PAYMENT_SUBMITTED: 'payment_submitted',
  PENDING: 'payment_submitted',
  PENDENTE: 'payment_submitted',
  PROCESSANDO: 'processing',
  PROCESSING: 'processing',
  READY: 'open',
  REJECTED: 'rejected',
  REJEITADO: 'rejected',
  SUCCESS: 'paid',
  TENTATIVA_ENVIADA: 'payment_submitted',
  WAITING_PAYMENT: 'payment_submitted',
};

export const onlyDigits = (value: string) =>
  String(value || '').replace(/\D/g, '');

export const formatPhoneNumber = (value: string) => {
  const rawDigits = onlyDigits(value);
  const digits = (
    rawDigits.length > 11 && rawDigits.startsWith('55')
      ? rawDigits.slice(2)
      : rawDigits
  ).slice(0, 11);

  if (digits.length <= 2) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;

  if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }

  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

export const parseDate = (value?: string) => {
  if (!value) return null;

  // Datas sem horário representam um dia civil. Interpretá-las como UTC
  // desloca a exibição para o dia anterior em fusos como America/Sao_Paulo.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) {
    const [, year, month, day] = dateOnly;
    const localDate = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(localDate.getTime()) ? null : localDate;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDateTime = (value?: string) => {
  const date = parseDate(value);
  return date ? dateTimeFormatter.format(date) : '-';
};

// Versão de uma linha para a tabela: "17/06/26 04:08".
export const formatDateTimeCompact = (value?: string) => {
  const date = parseDate(value);
  if (!date) return '-';
  return `${dateShortFormatter.format(date)} ${timeFormatter.format(date)}`;
};

export const formatDate = (value?: string) => {
  // Vencimento é um dia civil, mesmo quando a API o serializa como DATE em UTC.
  // Os horários de eventos continuam usando parseDate/formatDateTime.
  const civilDate = /^(\d{4})-(\d{2})-(\d{2})(?:T|$)/.exec(value || '');
  if (!civilDate) return '-';
  const [, year, month, day] = civilDate;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  if (
    date.getFullYear() !== Number(year) ||
    date.getMonth() !== Number(month) - 1 ||
    date.getDate() !== Number(day)
  )
    return '-';
  return date ? dateFormatter.format(date) : '-';
};

export const formatMoney = (value?: number | string) => {
  const amount = Number(value || 0);
  return currencyFormatter.format(Number.isFinite(amount) ? amount : 0);
};

export const normalizePaymentLinkStatus = (
  value: any,
  fallback = ''
): string => {
  const raw = String(value || '').trim();
  if (!raw) return fallback;

  if (statusConfig[raw]) return raw;

  const normalized = raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();

  return statusAliases[normalized] || fallback;
};

export const getEffectivePaymentLinkStatus = (item: any) => {
  const rawStatus = normalizePaymentLinkStatus(item?.status_raw, 'created');
  return normalizePaymentLinkStatus(
    item?.status_effective || item?.status,
    rawStatus
  );
};

export const withEffectivePaymentLinkStatus = (item: any) => {
  if (!item) return item;

  const statusOriginal = item.status_original || item.status;
  const statusEffective = getEffectivePaymentLinkStatus(item);

  return {
    ...item,
    status: statusEffective,
    status_effective: statusEffective,
    status_original: statusOriginal,
  };
};

export const sanitizeFilename = (value: string) =>
  String(value || 'link')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || 'link';

// Para o QR usamos o link curto (mais fácil de escanear); cai no completo se faltar.
export const isSafeHttpUrl = (value?: string) => {
  if (!value) return false;

  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch (_) {
    return false;
  }
};

export const getQrValue = (item: any) => {
  const value = item?.short_url || item?.link_url || '';
  return isSafeHttpUrl(value) ? value : '';
};

export const getStatusConfig = (status: string) =>
  statusConfig[status] || {
    label: status || 'N/A',
    summaryLabel: status || 'N/A',
    color: 'default' as StatusColor,
  };

export const getStatusDescription = (status: string) =>
  statusDescriptions[status] || 'Situação atual do link.';

export const getEventLabel = (eventType: string) =>
  eventLabels[eventType] || 'Evento registrado';
